import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePublisherAuth } from '../contexts/PublisherAuthContext';
import { useAuthPopup } from '../hooks/useAuthPopup';
import TokenIcon from './TokenIcon';

// Live token count pill — the web twin of the mobile app's TokenBadge.
const TokenBadge = ({ count }) => (
  <span className="flex items-center gap-1.5 bg-wp-surface border border-wp-border rounded-full px-3 py-1.5">
    <TokenIcon size={16} />
    <span className="text-wp-accent text-sm font-bold">{count.toLocaleString()}</span>
  </span>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  // Client auth state
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  // Publisher auth state
  const { isPublisherAuthenticated, logout: publisherLogout, isPublisherLoading } = usePublisherAuth();
  // Auth0 Universal Login popup — same flow the homepage buttons use.
  const { openAuthPopup, isLoading: isPopupOpening, error: popupError } = useAuthPopup();

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleLogin = () => {
    closeMenu();
    openAuthPopup('login');
  };

  const handleLogout = () => {
    if (isAuthenticated) {
      logout();
    } else if (isPublisherAuthenticated) {
      publisherLogout();
    }
    closeMenu();
  };

  const showLoginButton = !isLoading && !isPublisherLoading && !isAuthenticated && !isPublisherAuthenticated;
  const showLogoutButton = isAuthenticated || isPublisherAuthenticated;

  const linkStyles = "px-3 py-2 rounded-md text-sm font-medium text-wp-muted hover:text-white transition-colors";
  const activeLinkStyles = "text-wp-accent font-semibold";
  const mobileLinkStyles = "block px-3 py-2 rounded-md text-base font-medium text-wp-muted hover:text-white hover:bg-wp-surface";
  const mobileActiveLinkStyles = "text-wp-accent";

  return (
    <nav className="bg-wp-bg border-b border-wp-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center gap-2 text-2xl font-bold text-white" onClick={closeMenu}>
              <TokenIcon size={28} flat={false} />
              WallPass
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-2">
              <NavLink to="/" className={({ isActive }) => `${linkStyles} ${isActive ? activeLinkStyles : ''}`}>Home</NavLink>
              <NavLink to="/buy-tokens" className={({ isActive }) => `${linkStyles} ${isActive ? activeLinkStyles : ''}`}>Tokens</NavLink>
              {isAuthenticated && (
                <NavLink to="/profile" className={({ isActive }) => `${linkStyles} ${isActive ? activeLinkStyles : ''}`}>Profile</NavLink>
              )}
              {/* Roles come from /users/me; the API enforces admin independently. */}
              {isAuthenticated && user?.roles?.includes('admin') && (
                <NavLink to="/admin" className={({ isActive }) => `${linkStyles} ${isActive ? activeLinkStyles : ''}`}>Admin</NavLink>
              )}
              {isPublisherAuthenticated && (
                <NavLink to="/publisher/dashboard" className={({ isActive }) => `${linkStyles} ${isActive ? activeLinkStyles : ''}`}>Publisher Dashboard</NavLink>
              )}
              {isAuthenticated && user && (
                <span className="ml-2"><TokenBadge count={user.wallpassTokenBalance ?? 0} /></span>
              )}
              {!isLoading && !isPublisherLoading && (
                showLoginButton ? (
                  <div className="relative ml-4">
                    <button
                      onClick={handleLogin}
                      disabled={isPopupOpening}
                      className="px-4 py-2 rounded-xl text-sm font-semibold text-wp-bg bg-wp-accent hover:opacity-80 transition-opacity disabled:opacity-50"
                    >
                      Log in
                    </button>
                    {/* The nav has no room for an error row — float it under the button. */}
                    {popupError && (
                      <p className="absolute right-0 top-full mt-2 w-56 bg-wp-surface text-wp-danger text-xs rounded-lg px-3 py-2 shadow-lg">
                        {popupError}
                      </p>
                    )}
                  </div>
                ) : ( showLogoutButton &&
                  <button onClick={handleLogout} className="ml-4 px-4 py-2 rounded-xl text-sm font-semibold text-wp-danger border border-wp-danger hover:bg-wp-surface transition-colors">Log Out</button>
                )
              )}
            </div>
          </div>

          {/* Mobile: badge + hamburger */}
          <div className="flex items-center gap-3 md:hidden">
            {isAuthenticated && user && <TokenBadge count={user.wallpassTokenBalance ?? 0} />}
            <button onClick={toggleMenu} className="inline-flex items-center justify-center p-2 rounded-md text-wp-muted hover:text-white hover:bg-wp-surface focus:outline-none">
              <span className="sr-only">Open main menu</span>
              {isOpen ? <i className="fas fa-times h-6 w-6 block"></i> : <i className="fas fa-bars h-6 w-6 block"></i>}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`${isOpen ? 'block' : 'hidden'} md:hidden border-t border-wp-border`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <NavLink to="/" className={({ isActive }) => `${mobileLinkStyles} ${isActive ? mobileActiveLinkStyles : ''}`} onClick={closeMenu}>Home</NavLink>
          <NavLink to="/buy-tokens" className={({ isActive }) => `${mobileLinkStyles} ${isActive ? mobileActiveLinkStyles : ''}`} onClick={closeMenu}>Tokens</NavLink>
          {isAuthenticated && (
            <NavLink to="/profile" className={({ isActive }) => `${mobileLinkStyles} ${isActive ? mobileActiveLinkStyles : ''}`} onClick={closeMenu}>Profile</NavLink>
          )}
          {isAuthenticated && user?.roles?.includes('admin') && (
            <NavLink to="/admin" className={({ isActive }) => `${mobileLinkStyles} ${isActive ? mobileActiveLinkStyles : ''}`} onClick={closeMenu}>Admin</NavLink>
          )}
          {isPublisherAuthenticated && (
            <NavLink to="/publisher/dashboard" className={({ isActive }) => `${mobileLinkStyles} ${isActive ? mobileActiveLinkStyles : ''}`} onClick={closeMenu}>Publisher Dashboard</NavLink>
          )}
        </div>
        <div className="pt-4 pb-3 border-t border-wp-border">
          <div className="px-2">
            {!isLoading && !isPublisherLoading && (
              showLoginButton ? (
                <>
                  <button
                    onClick={handleLogin}
                    disabled={isPopupOpening}
                    className="w-full px-3 py-2 rounded-xl text-base font-semibold text-wp-bg bg-wp-accent hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    Log in
                  </button>
                  {popupError && <p className="text-wp-danger text-xs mt-2 px-1">{popupError}</p>}
                </>
              ) : ( showLogoutButton &&
                <button onClick={handleLogout} className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-wp-danger hover:bg-wp-surface">Log Out</button>
              )
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
